'use client';

import { useRouter } from '@bprogress/next';
import { CheckCircle } from '@c/icons/CheckCircle';
import { BADGE_CONFIG } from '@c/InformationBadge';
import type { BreadcrumbItem } from '@ui/Breadcrumb';
import SafeImage from '@ui/SafeImage';
import { prefix } from '@util/imagePrefix';
import { isMobile } from '@util/index';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import { AlertCircleIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import type { ProductDocument } from 'models/product';
import type { UserBike } from 'models/shared';
import { useState } from 'react';
import DesktopSearchByVehicle from './SearchByVehicle/DesktopSearchByVehicle';
import MobileSearchByVehicle from './SearchByVehicle/MobileSearchByVehicle';

type ConfirmFitmentProps = {
  product: ProductDocument;
  breadcrumbs?: BreadcrumbItem[];
};

export default function ConfirmFitment({
  product,
  breadcrumbs,
}: ConfirmFitmentProps) {
  const router = useRouter();
  const { selectedBike, setSelectedBike, clearSelectedBike } = useBikeFilter();
  const { userDoc } = useAuth();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  const isBikeComplete = Boolean(
    selectedBike.type &&
      selectedBike.make &&
      selectedBike.year &&
      selectedBike.model
  );

  const fitment = selectedBike.type
    ? getBikeFitment(product.fitting_bikes?.bikes ?? [], [
        selectedBike as UserBike,
      ])
    : null;

  const handleBikeComplete = (bike: UserBike) => {
    if (bike.type && bike.make && bike.model && bike.year) {
      setSelectedBike(bike);
    }
  };

  const handleFindItems = () => {
    if (
      !selectedBike.type ||
      !selectedBike.make ||
      !selectedBike.model ||
      !selectedBike.year
    )
      return;

    const year = selectedBike.year;
    const make = selectedBike.make;
    const model = selectedBike.model;
    const bikeParam = `?bikes=${year}:${make}:${model}`;

    router.push(
      `${breadcrumbs?.[1] ? breadcrumbs[1].href : '/shop/all'}${bikeParam}`
    );
  };

  const FitmentMessage = () => {
    if (fitment?.status === 'fits') {
      return (
        <div className="flex flex-col items-start sm:flex-row sm:gap-x-2 sm:gap-y-0">
          <div className="flex items-start gap-x-2">
            <CheckCircle
              className="h-[2.5rem] w-[2.5rem]"
              fill={BADGE_CONFIG.success.foreground}
            />
            <span className="w-[35rem] truncate pt-1 text-[1.6rem] font-medium">
              Fits your vehicle
            </span>
          </div>
        </div>
      );
    }

    if (fitment?.status === 'may-fit') {
      return (
        <div className="flex flex-col items-start gap-y-2 sm:flex-row sm:gap-x-2 sm:gap-y-0">
          <div className="flex items-start gap-x-2">
            <AlertCircleIcon
              className="h-[2.5rem] w-[2.5rem]"
              style={{ color: BADGE_CONFIG.warning.foreground }}
            />
            <span className="w-[35rem] truncate pt-1 text-[1.6rem] font-medium">
              May fit your {selectedBike.make} {selectedBike.year}{' '}
              {selectedBike.model}.
            </span>
          </div>
        </div>
      );
    }

    if (fitment?.status === 'does-not-fit') {
      return (
        <div className="flex flex-row items-center gap-x-2">
          <div className="flex items-start gap-x-2">
            <XCircleIcon
              className="h-[2.5rem] w-[2.5rem]"
              style={{ color: BADGE_CONFIG.error.foreground }}
            />
            <span className="text-[1.6rem] font-medium">
              Does not fit your vehicle
            </span>
          </div>

          <div className="h-2 w-2 rounded-full bg-zinc-400" />

          <button
            type="button"
            onClick={handleFindItems}
            className="flex items-center gap-x-2 whitespace-nowrap text-[1.6rem] font-bold text-black underline"
          >
            <span className="sm:hidden">Find parts</span>
            <span className="hidden sm:inline">Find parts that will fit</span>
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      );
    }

    return null;
  };

  const parsedBrandName = isBikeComplete
    ? selectedBike.make === 'Gas Gas'
      ? selectedBike.make.split(' ').join('').toLowerCase()
      : selectedBike.make?.split(' ').join('-').toLowerCase() ?? ''
    : '';
  const brandImage = isBikeComplete
    ? `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`
    : '';

  // Create a unique key for the image to force re-render when vehicle changes
  const imageKey = isBikeComplete
    ? `${selectedBike.make}-${selectedBike.model}-${selectedBike.year}`
    : 'no-vehicle';

  return (
    <div className="-mt-8 mb-0 sm:mb-2 sm:mt-0 sm:p-4">
      <div className="flex w-full flex-col items-center justify-between gap-y-8 rounded-[2rem] border border-zinc-200 bg-zinc-100 p-[2rem] pb-4 sm:flex-row sm:items-center sm:pb-[2rem]">
        <div className="flex w-full flex-col gap-y-2">
          {isBikeComplete ? (
            <div className="flex items-center gap-x-4">
              <SafeImage
                key={imageKey}
                alt={selectedBike.make ?? ''}
                src={brandImage}
                fallback={
                  <div className="flex h-32 w-32 items-center justify-center rounded-[0.5rem] bg-zinc-200 text-center text-[1rem] font-medium text-zinc-600">
                    {selectedBike.make}
                  </div>
                }
                height={128}
                width={128}
                className="h-32 w-32 shrink-0 rounded-[1rem] object-contain"
              />
              <div className="flex flex-col">
                <span className="text-[2rem] font-semibold leading-tight text-black">
                  {selectedBike.year} {selectedBike.make} {selectedBike.model}
                </span>
              </div>
            </div>
          ) : (
            <>
              <h3 className="text-[2.1rem] font-semibold text-black">
                Confirm this item fits
              </h3>

              <h3 className="text-[1.8rem] text-zinc-500">
                Add your vehicle details to make sure this item fits.
              </h3>
            </>
          )}

          {isBikeComplete && (
            <div className="mt-4">
              <FitmentMessage />
            </div>
          )}
        </div>

        <div className="flex w-full flex-col items-center gap-y-4 sm:w-auto sm:gap-y-8">
          <button
            type="button"
            onClick={() => {
              if (isMobile()) {
                setShowMobileSearchByVehicle(true);
              } else {
                setShowDesktopSearchByVehicle(true);
              }
            }}
            className="flex w-full items-center justify-center whitespace-nowrap rounded-full bg-black px-24 py-7 text-[1.8rem] font-semibold text-white transition-colors hover:bg-gray-800 sm:w-fit"
          >
            {isBikeComplete ? 'Change vehicle' : 'Select vehicle'}
          </button>

          <MobileSearchByVehicle
            show={showMobileSearchByVehicle}
            dismiss={() => setShowMobileSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
            onClear={clearSelectedBike}
            initialBike={selectedBike}
            userDoc={userDoc}
          />

          <DesktopSearchByVehicle
            isOpen={showDesktopSearchByVehicle}
            dismiss={() => setShowDesktopSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
            onClear={clearSelectedBike}
            initialBike={selectedBike}
          />
        </div>
      </div>
    </div>
  );
}
